import React, { useEffect, useRef, useState } from 'react';
import useDeferredRowChange from './useDeferredRowChange';

/**
 * Editable text cell with per-row add/delete icons revealed inline on hover —
 * mirrors the legacy Average-Costing quantity cell (icons sit to the left of the
 * input, shown on row hover). Add shows when getCanAdd(row), delete when
 * getCanDelete(row).
 *
 * cellRendererParams (merged with column.cellProps):
 *   onRowChange(rowId, field, value)
 *   getRowId(row) -> string
 *   getCanAdd(row)    -> boolean
 *   getCanDelete(row) -> boolean
 *   onAdd(row)
 *   onDelete(row)
 *   disabled
 *   error
 *   placeholder       falls back to the column's headerName, then 'Text Box'
 *   type             'text' | 'number'  (default 'text'); 'number' restricts input to digits, '.' and '-'
 */
function HierActionInputCell(params) {
  const { value, data, colDef, onRowChange, getRowId, getCanAdd, getCanDelete, onAdd, onDelete, disabled, error, placeholder, type = 'text' } = params;
  const rowId = getRowId ? getRowId(data) : data?.id;
  const field = colDef?.field;
  const canAdd = typeof getCanAdd === 'function' ? getCanAdd(data) : false;
  const canDelete = typeof getCanDelete === 'function' ? getCanDelete(data) : false;

  const focusedRef = useRef(false);
  const [local, setLocal] = useState(value ?? '');
  const [commitChange, flushChange] = useDeferredRowChange(onRowChange);

  useEffect(() => {
    if (!focusedRef.current) setLocal(value ?? '');
  }, [value]);

  /**
   * Handles input changes, restricting to numeric characters when type is 'number',
   * then stores the local value and schedules a deferred row commit.
   * @param {object} event - The input change event.
   */
  const handleChange = (event) => {
    const nextValue = type === 'number' ? event.target.value.replace(/[^0-9.\-]/g, '') : event.target.value;
    setLocal(nextValue);
    commitChange(rowId, field, nextValue);
  };

  return (
    <div className={`cag-cell-wrap${error ? ' cag-cell-wrap--error' : ''}`}>
      <div className="cag-hier-input-row">
        {(canAdd || canDelete) && (
          <div className="cag-hier-inline-actions">
            {canAdd && (
              <button type="button" className="bombiix-icon-container bombiix-icon-container--blue" title="Add row" aria-label="Add row" onClick={() => onAdd?.(data)}>
                <i className="bombiix-icon-add" />
              </button>
            )}
            {canDelete && (
              <button type="button" className="bombiix-icon-container bombiix-icon-container--red" title="Delete row" aria-label="Delete row" onClick={() => onDelete?.(data)}>
                <i className="bombiix-icon-delete" />
              </button>
            )}
          </div>
        )}
        <input
          className="cag-cell-input"
          type="text"
          inputMode="decimal"
          value={local}
          placeholder={placeholder ?? colDef?.headerName ?? 'Text Box'}
          disabled={disabled}
          onFocus={() => {
            focusedRef.current = true;
          }}
          onBlur={() => {
            focusedRef.current = false;
            flushChange();
            setLocal(value ?? '');
          }}
          onChange={handleChange}
        />
      </div>
      {error ? <span className="cag-cell-error">{error}</span> : null}
    </div>
  );
}

export default HierActionInputCell;
